export type ComponentConfig = Record<string, unknown>;

const DATA_PREFIX = 'antumo';
const instanceRegistry = new Map<Element, Map<string, BaseComponent>>();

function kebabToCamel(str: string): string {
  return str.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
}

function parseValue(value: string): unknown {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value);
  return value;
}

export class BaseComponent {
  protected _element!: Element | null;
  protected _config!: ComponentConfig;

  static DefaultConfig: ComponentConfig = {};

  static get NAME(): string { return 'base'; }

  static get DATA_KEY(): string { return `antumo.${this.NAME}`; }

  constructor(element: Element, config: ComponentConfig = {}) {
    if (!element) {
      throw new TypeError(
        `${(this.constructor as typeof BaseComponent).NAME}: element is required`
      );
    }

    const Ctor = this.constructor as typeof BaseComponent;
    const existing = Ctor.getInstance(element);
    if (existing) return existing as this;

    this._element = element;
    this._config = this._getConfig(config);

    let componentMap = instanceRegistry.get(element);
    if (!componentMap) {
      componentMap = new Map();
      instanceRegistry.set(element, componentMap);
    }
    componentMap.set(Ctor.DATA_KEY, this);
  }

  get element(): Element | null { return this._element; }

  get config(): ComponentConfig { return this._config; }

  dispose(): void {
    const Ctor = this.constructor as typeof BaseComponent;
    const componentMap = instanceRegistry.get(this._element!);
    if (componentMap) {
      componentMap.delete(Ctor.DATA_KEY);
      if (componentMap.size === 0) {
        instanceRegistry.delete(this._element!);
      }
    }
    this._element = null;
  }

  static getInstance(element: Element): BaseComponent | null {
    const componentMap = instanceRegistry.get(element);
    return componentMap?.get(this.DATA_KEY) ?? null;
  }

  static getOrCreateInstance(
    element: Element,
    config?: ComponentConfig
  ): BaseComponent {
    return this.getInstance(element) ?? new this(element, config);
  }

  protected _getConfig(config: ComponentConfig): ComponentConfig {
    const Ctor = this.constructor as typeof BaseComponent;
    return {
      ...Ctor.DefaultConfig,
      ...this._getDataAttributes(),
      ...config,
    };
  }

  protected _getDataAttributes(): ComponentConfig {
    if (!this._element || !(this._element instanceof HTMLElement)) return {};

    const attrs: ComponentConfig = {};
    const prefix = `data-${DATA_PREFIX}-`;

    for (const attr of Array.from(this._element.attributes)) {
      if (attr.name.startsWith(prefix)) {
        const key = kebabToCamel(attr.name.slice(prefix.length));
        attrs[key] = parseValue(attr.value);
      }
    }
    return attrs;
  }
}
